import {CommonModule} from '@angular/common';
import {NgModule, Optional} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NavigationEnd, Router} from '@angular/router';
import {HttpClientModule} from '@angular/common/http';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';

import {LayoutService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {ErrorService} from '@ngmedax/error';
import {Translatable, TranslationService} from '@ngmedax/translation';

import {configKeys} from './thieme-catalog.config-keys';
import {routing} from './thieme-catalog.routing';
import {NAVIGATION} from './thieme-catalog.navigation';
import {ThiemeCatalogGridComponent} from './components/catalog-grid/thieme-catalog-grid.component';
import {ThiemeCatalogService} from './services/thieme-catalog.service';
import {ThiemeImportService} from './services/thieme-import.service';
import {AuthUriService} from './services/auth-uri.service';
import {ApiService} from './services/api.service';
import {TRANSLATION_GRID_SCOPE} from '../../constants';
import {KEYS} from '../../translation-keys';
import {filter} from 'rxjs/operators';


// hack to inject decorator declarations. must occur before class declaration!
export interface ThiemeCatalogModule extends Translatable {}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    NgbModule,
    routing
  ],
  declarations: [
    ThiemeCatalogGridComponent
  ],
  exports: [],
  providers: [
    ThiemeCatalogService,
    ThiemeImportService,
    AuthUriService,
    ApiService
  ]
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class ThiemeCatalogModule {
  public constructor(
    private router: Router,
    private layoutService: LayoutService,
    private configService: ConfigService,
    private errorService: ErrorService,
    @Optional() private translationService: TranslationService,
  ) {
    // menu entry is always registered - the permission module filters it against
    // the route data (needsConfig feature.thieme), so it is only visible when the
    // instance license carries the thieme feature
    layoutService.addMenuEntry(NAVIGATION);

    // subscribe to navigation end event: redirect to the error page when the
    // thieme feature is licensed but the connector api uri is not configured
    const subscription = router.events
      .pipe(<any>filter((event: Event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.configService.get(configKeys.FEATURE_THIEME_CONFIG_KEY)) {
          return;
        }

        if (!this.configService.get(configKeys.THIEME_CONNECTOR_URI_CONFIG_KEY)) {
          // unsubscribe to prevent redirect loop
          subscription.unsubscribe();
          this.showErrorMessage();
        }
      });
  }

  /**
   * Adds a "thieme connector api url not found" message to the error service and redirects to the error page.
   */
  private showErrorMessage() {
    // uri for the error message
    const uri = 'thieme-connector-api-uri-not-configured';

    // title that we will see on the error page
    const title = this._(KEYS.GRID.CONFIG_ERROR);

    // message that we will see on the error page
    const message = this._(KEYS.GRID.ERROR_MISSING_MANDATORY_CONFIG) + '<br/><br/>';

    // example config object that we will se on the error page
    const exampleObject = {
      'apis': {
        'thieme': {
          'connector': {
            'uri': 'https://some.tld/api/module/thieme-connector'
          }
        }
      }
    };

    // add error message and redirect to error page
    // note: error service will only redirect when we are not already on this error page
    this.errorService
      .addErrorMessage(uri, title, message, exampleObject)
      .navigateToError(uri);
  }
}
